import { useCallback } from 'react';
import { Flex, RemoteImage, Typography, useScreenSize } from '@banhala/ui-kit';
import type { EventGridProps, EventGridEntity } from './types';
import { bridge, isNotNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

const HORIZONTAL_PADDING = 12;
const GAP = 2;

export const EventGrid = ({ entity, logging }: EventGridProps) => {
  const { width: screenWidth } = useScreenSize();

  if (!entity || entity.length === 0) return null;

  const itemSize = Math.floor(
    (screenWidth - HORIZONTAL_PADDING * 2 - GAP * 2) / 3,
  );
  const bigSize = itemSize * 2 + GAP;

  // 최대 6개
  const items = entity.slice(0, 6);

  // 레이아웃: [큰(0)] + [작은(1), 작은(2)] 상단 / [작은(3), 작은(4), 작은(5)] 하단
  const topRight = items.slice(1, 3);
  const bottomRow = items.slice(3, 6);

  return (
    <Flex padding={{ horizontal: HORIZONTAL_PADDING }}>
      {/* 상단: 큰 이미지 + 우측 2개 */}
      <Flex.Row gap={GAP}>
        {isNotNil(items[0]) && (
          <GridItem
            entity={items[0]}
            logging={logging}
            width={bigSize}
            height={bigSize}
            isBig
          />
        )}
        <Flex flex={1} gap={GAP}>
          {topRight.map(item => (
            <GridItem
              key={item.item.deeplink}
              entity={item}
              logging={logging}
              width={itemSize}
              height={itemSize}
            />
          ))}
        </Flex>
      </Flex.Row>
      {/* 하단: 3개 */}
      {bottomRow.length > 0 && (
        <Flex.Row gap={GAP} margin={{ top: GAP }}>
          {bottomRow.map(item => (
            <GridItem
              key={item.item.deeplink}
              entity={item}
              logging={logging}
              width={itemSize}
              height={itemSize}
            />
          ))}
        </Flex.Row>
      )}
    </Flex>
  );
};

const GridItem = ({
  entity,
  logging,
  width,
  height,
  isBig = false,
}: {
  entity: EventGridEntity;
  logging: IScreensLogging;
  width: number;
  height: number;
  isBig?: boolean;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_EVENT_GRID',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: {
        ...logging?.ably_only_log,
        ...entity.logging?.ably_only_log,
      },
    });
    if (item.deeplink) {
      openDeeplink(item.deeplink);
    }
  }, [item.deeplink, logging, entity.logging, openDeeplink]);

  return (
    <Flex
      width={width}
      height={height}
      borderRadius={4}
      overflow="hidden"
      onPress={handlePress}
    >
      <RemoteImage
        uri={item.image}
        width={width}
        height={height}
        resizeMode="cover"
      />
      <Flex
        position={'absolute'}
        bottom={0}
        left={0}
        right={0}
        height={'50%'}
        backgroundColor={'black'}
        backgroundColorOpacity={0.35}
        justifyContent={'flex-end'}
      >
        <Flex padding={{ horizontal: isBig ? 12 : 8, bottom: isBig ? 12 : 8 }}>
          <Typography
            type={isBig ? 'h4' : 'subtitle2'}
            color="content_always_white"
            numberOfLines={2}
          >
            {item.title}
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
